/**
 * Analytics — strategy backtesting.
 *
 * POST /backtests/run replays a caller-supplied price series; there is no
 * historical data source wired in, so prices are pasted by the user.
 */

import { useState } from 'react';
import { BarChart3, LineChart, Play, TrendingDown, TrendingUp } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, getErrorMessage } from '@/config/api';
import { CHART, CHART_AXIS, CHART_TOOLTIP } from '@/lib/chart';
import { formatINR, formatNumber, formatPct, formatQty, signClass, toNum } from '@/lib/format';
import { cn } from '@/lib/cn';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  StatCard,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
} from '@/components/ui';

interface BacktestMetrics {
  total_return_pct: number;
  net_pnl: string;
  sharpe_ratio: number;
  sortino_ratio: number;
  max_drawdown_pct: number;
  calmar_ratio: number;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  win_rate_pct: number;
  profit_factor: number;
  avg_win: string;
  avg_loss: string;
  largest_win: string;
  largest_loss: string;
  avg_holding_periods: number;
  initial_capital: string;
  final_capital: string;
  total_commission: string;
}

interface SimTrade {
  trade_id: string;
  signal_type: string;
  entry_price: string;
  exit_price: string;
  quantity: number;
  net_pnl: string;
  holding_periods: number;
}

interface BacktestResult {
  backtest_id: string;
  status: string;
  signals_generated: number;
  duration_ms: number;
  metrics: BacktestMetrics;
  trades: SimTrade[];
  equity_curve: Array<{ index: number; equity: string; drawdown_pct: string }>;
}

const backtestSchema = z.object({
  strategy_type: z.string().min(1, 'Required'),
  symbol: z.string().min(1, 'Required'),
  prices_csv: z.string().min(1, 'Enter comma-separated prices'),
  initial_capital: z.coerce.number().positive().default(1_000_000),
  position_size: z.coerce.number().int().positive().default(100),
  commission: z.coerce.number().min(0).default(20),
  slippage_pct: z.coerce.number().min(0).max(5).default(0.05),
});

type BacktestFormData = z.infer<typeof backtestSchema>;

const STRATEGY_TYPES = [
  { value: 'ema_crossover', label: 'EMA crossover' },
  { value: 'rsi_mean_reversion', label: 'RSI mean reversion' },
  { value: 'macd_momentum', label: 'MACD momentum' },
  { value: 'bollinger_bands', label: 'Bollinger bands' },
  { value: 'vwap', label: 'VWAP' },
];

const MIN_PRICE_POINTS = 30;

export default function AnalyticsPage() {
  const [result, setResult] = useState<BacktestResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<BacktestFormData>({
    resolver: zodResolver(backtestSchema) as never,
    defaultValues: {
      strategy_type: 'ema_crossover',
      symbol: 'RELIANCE',
      prices_csv: '',
      initial_capital: 1_000_000,
      position_size: 100,
      commission: 20,
      slippage_pct: 0.05,
    },
  });

  const pricesCsv = watch('prices_csv');
  const priceCount = pricesCsv
    ? pricesCsv.split(',').map((p) => parseFloat(p.trim())).filter((p) => !Number.isNaN(p)).length
    : 0;

  const onSubmit = async (data: BacktestFormData) => {
    setIsRunning(true);
    setError(null);
    setResult(null);
    try {
      const prices = data.prices_csv
        .split(',')
        .map((p) => parseFloat(p.trim()))
        .filter((p) => !Number.isNaN(p));

      if (prices.length < MIN_PRICE_POINTS) {
        setError(`Need at least ${MIN_PRICE_POINTS} price points for a meaningful backtest`);
        return;
      }

      const { data: res } = await api.post('/backtests/run', {
        strategy_type: data.strategy_type,
        symbol: data.symbol,
        prices,
        initial_capital: data.initial_capital,
        position_size: data.position_size,
        commission_per_order: data.commission,
        slippage_pct: data.slippage_pct,
      });
      setResult(res);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsRunning(false);
    }
  };

  const m = result?.metrics;
  const equityData =
    result?.equity_curve.map((e) => ({
      index: e.index,
      equity: toNum(e.equity),
      drawdown: toNum(e.drawdown_pct),
    })) ?? [];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={BarChart3}
        title="Analytics"
        description="Backtest a strategy against your own price series"
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* Configuration */}
        <Card className="xl:col-span-1">
          <CardHeader title="Backtest setup" description="No historical feed is wired in — paste a price series" />
          <CardBody>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
              <Field label="Strategy" error={errors.strategy_type?.message}>
                {(field) => (
                  <Select {...field} {...register('strategy_type')}>
                    {STRATEGY_TYPES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label="Symbol" error={errors.symbol?.message}>
                {(field) => <Input {...field} placeholder="RELIANCE" {...register('symbol')} />}
              </Field>

              <Field
                label="Price series"
                error={errors.prices_csv?.message}
                hint={`Comma-separated closes · ${priceCount} of ${MIN_PRICE_POINTS} minimum`}
              >
                {(field) => (
                  <Textarea
                    {...field}
                    rows={5}
                    className="font-tabular"
                    placeholder="2450.5, 2455.0, 2461.25, …"
                    {...register('prices_csv')}
                  />
                )}
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Initial capital" error={errors.initial_capital?.message}>
                  {(field) => <Input {...field} type="number" min={1} mono {...register('initial_capital')} />}
                </Field>
                <Field label="Position size" error={errors.position_size?.message}>
                  {(field) => <Input {...field} type="number" min={1} mono {...register('position_size')} />}
                </Field>
                <Field label="Commission" error={errors.commission?.message}>
                  {(field) => <Input {...field} type="number" min={0} mono {...register('commission')} />}
                </Field>
                <Field label="Slippage %" error={errors.slippage_pct?.message}>
                  {(field) => (
                    <Input {...field} type="number" step="0.01" min={0} max={5} mono {...register('slippage_pct')} />
                  )}
                </Field>
              </div>

              <Button type="submit" variant="primary" icon={Play} loading={isRunning} fullWidth>
                {isRunning ? 'Running backtest…' : 'Run backtest'}
              </Button>
            </form>
          </CardBody>
        </Card>

        {/* Results */}
        <div className="flex flex-col gap-4 xl:col-span-2">
          {error && <Alert tone="danger">{error}</Alert>}

          {!result && !error && (
            <Card className="flex min-h-72 items-center justify-center">
              <EmptyState
                icon={LineChart}
                title="No backtest run yet"
                description={`Paste at least ${MIN_PRICE_POINTS} closing prices and run the strategy to see its equity curve, metrics and simulated trades.`}
              />
            </Card>
          )}

          {result && m && (
            <>
              <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                <StatCard
                  label="Net P&L"
                  value={<span className={signClass(m.net_pnl)}>{formatINR(m.net_pnl)}</span>}
                  delta={{
                    value: formatPct(m.total_return_pct),
                    direction: m.total_return_pct > 0 ? 1 : m.total_return_pct < 0 ? -1 : 0,
                  }}
                />
                <StatCard label="Sharpe" value={formatNumber(m.sharpe_ratio)} hint={`Sortino ${formatNumber(m.sortino_ratio)}`} />
                <StatCard
                  label="Max drawdown"
                  value={<span className="text-loss">{formatPct(-Math.abs(m.max_drawdown_pct))}</span>}
                  hint={`Calmar ${formatNumber(m.calmar_ratio)}`}
                />
                <StatCard
                  label="Win rate"
                  value={formatPct(m.win_rate_pct)}
                  hint={`${formatQty(m.winning_trades)}W / ${formatQty(m.losing_trades)}L`}
                />
              </div>

              <Card>
                <CardHeader
                  title="Equity curve"
                  description={`${result.signals_generated} signals · ${formatQty(result.duration_ms)} ms`}
                  actions={<Badge tone={result.status === 'completed' ? 'success' : 'warning'}>{result.status}</Badge>}
                />
                <CardBody>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={equityData} margin={{ top: 4, right: 4, bottom: 0, left: -8 }}>
                        <defs>
                          <linearGradient id="bt-equity" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={CHART.brand} stopOpacity={0.22} />
                            <stop offset="100%" stopColor={CHART.brand} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke={CHART.grid} vertical={false} />
                        <XAxis dataKey="index" {...CHART_AXIS} />
                        <YAxis {...CHART_AXIS} width={56} tickFormatter={(v: number) => `₹${(v / 1000).toFixed(0)}k`} />
                        <Tooltip
                          contentStyle={CHART_TOOLTIP}
                          formatter={(value: unknown) => [formatINR(value as number), 'Equity']}
                        />
                        <Area
                          type="monotone"
                          dataKey="equity"
                          stroke={CHART.brand}
                          strokeWidth={2}
                          fill="url(#bt-equity)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardHeader title="Performance detail" />
                <CardBody>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                    {[
                      ['Initial capital', formatINR(m.initial_capital)],
                      ['Final capital', formatINR(m.final_capital)],
                      ['Total trades', formatQty(m.total_trades)],
                      ['Profit factor', formatNumber(m.profit_factor)],
                      ['Average win', formatINR(m.avg_win)],
                      ['Average loss', formatINR(m.avg_loss)],
                      ['Largest win', formatINR(m.largest_win)],
                      ['Largest loss', formatINR(m.largest_loss)],
                      ['Total commission', formatINR(m.total_commission)],
                    ].map(([label, value]) => (
                      <div key={label} className="flex justify-between gap-3 border-b border-line pb-2">
                        <dt className="text-fg-subtle">{label}</dt>
                        <dd className="font-tabular text-fg">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </CardBody>
              </Card>

              <Card>
                <CardHeader title="Simulated trades" description={`${result.trades.length} trades`} />
                {result.trades.length === 0 ? (
                  <EmptyState
                    icon={TrendingUp}
                    title="No trades generated"
                    description="The strategy produced no entries for this price series."
                  />
                ) : (
                  <TableWrap>
                    <Table>
                      <THead>
                        <TR>
                          <TH>Signal</TH>
                          <TH align="right">Entry</TH>
                          <TH align="right">Exit</TH>
                          <TH align="right">Qty</TH>
                          <TH align="right">Holding</TH>
                          <TH align="right">Net P&L</TH>
                        </TR>
                      </THead>
                      <TBody>
                        {result.trades.map((trade) => {
                          const profitable = toNum(trade.net_pnl) >= 0;
                          return (
                            <TR key={trade.trade_id}>
                              <TD>
                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-fg-muted">
                                  {profitable ? (
                                    <TrendingUp className="h-3 w-3 text-profit" aria-hidden="true" />
                                  ) : (
                                    <TrendingDown className="h-3 w-3 text-loss" aria-hidden="true" />
                                  )}
                                  {trade.signal_type}
                                </span>
                              </TD>
                              <TD align="right" className="font-tabular text-fg-muted">
                                {formatNumber(trade.entry_price)}
                              </TD>
                              <TD align="right" className="font-tabular text-fg-muted">
                                {formatNumber(trade.exit_price)}
                              </TD>
                              <TD align="right" className="font-tabular text-fg-muted">
                                {formatQty(trade.quantity)}
                              </TD>
                              <TD align="right" className="font-tabular text-fg-subtle">
                                {formatQty(trade.holding_periods)}
                              </TD>
                              <TD align="right" className={cn('font-tabular font-medium', signClass(trade.net_pnl))}>
                                {formatINR(trade.net_pnl)}
                              </TD>
                            </TR>
                          );
                        })}
                      </TBody>
                    </Table>
                  </TableWrap>
                )}
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
